/**
 * Legacy `<Badge>` and `<SimpleBadge>` — now thin wrappers over the
 * FSD `<Badge>` primitive. Keeps the old API surface
 * (`colorType`, `customStyle`, `fixedWidth`, `variant`, `onClick`,
 * `BADGES_COLOR_TYPES`) so existing callsites compile unchanged,
 * but the visual layer comes from the design-system `qhs-badge--*`
 * palette: light surface + darker text, restrained sizes, no
 * uppercase / heavy weight that the original MUI <Chip> shipped.
 *
 * Mapping the old colour keys into the FSD tone palette is done
 * via `LEGACY_TO_FSD_TONE` below. If the caller is already passing
 * an FSD tone string ('normal', 'critical', 'sage', …), it falls
 * through unchanged — `getStatusBadge` and `getTagsColor` were
 * updated 2026-05-05 to return FSD tones directly.
 */
import PropTypes from 'prop-types';
import FSDBadge from '@shared/ui/Badge';

// Stable export so callers that destructure
// `BADGES_COLOR_TYPES.green` keep compiling. Values are the legacy
// keys the wrapper still understands; LEGACY_TO_FSD_TONE maps them
// onto FSD tones.
export const BADGES_COLOR_TYPES = {
  green: 'green',
  green2: 'green2',
  blue: 'blue',
  grey: 'grey',
  lightGrey: 'lightGrey',
  lightGrey2: 'lightGrey2',
  red: 'red',
  orange: 'orange',
  lightBlue: 'lightBlue',
  purple: 'purple',
  pink: 'pink',
};

// Coarse fallback when a legacy colour key is fed straight in (e.g.
// `colorType={BADGES_COLOR_TYPES.green}`). The semantics here are
// approximate — `green` becomes 'normal' (success), `red` becomes
// 'critical', neutrals collapse to 'inactive', etc. Code paths that
// want the fully audited mapping should use `getStatusBadge` /
// `getTagsColor` from `@shared/lib/getStatusBadge` and pass their
// FSD-tone result straight through.
const LEGACY_TO_FSD_TONE = {
  green: 'normal',
  green2: 'normal',
  blue: 'pending',
  lightBlue: 'pending',
  grey: 'inactive',
  lightGrey: 'inactive',
  lightGrey2: 'inactive',
  red: 'critical',
  orange: 'high',
  purple: 'wine',
  pink: 'wine',
};

// Kept for re-export so the legacy file's named imports
// (`COLOR_BY_TYPES`) still resolve.
export const COLOR_BY_TYPES = BADGES_COLOR_TYPES;

const resolveTone = key => {
  if (!key) return 'inactive';
  // FSD tone passthrough.
  if (key in LEGACY_TO_FSD_TONE) return LEGACY_TO_FSD_TONE[key];
  return key;
};

export const Badge = ({
  label,
  colorType,
  customStyle,
  // Legacy-only props that the FSD `<Badge>` doesn't expose — kept
  // in the signature so old callsites compile but otherwise
  // ignored. fixedWidth and the diagonal radii were rarely used
  // and the cleaner design-system shapes win out by default.
  fixedWidth = false, // eslint-disable-line no-unused-vars
  diagonalBorderRadius = false, // eslint-disable-line no-unused-vars
  bottomLeftRadius = false, // eslint-disable-line no-unused-vars
  variant, // eslint-disable-line no-unused-vars
  onClick,
  ...rest
}) => {
  const tone = resolveTone(colorType);
  return (
    <FSDBadge tone={tone} className={customStyle} onClick={onClick} {...rest}>
      {label}
    </FSDBadge>
  );
};

// `SimpleBadge` previously rendered a slightly different `<div>`
// shape (smaller font, no MUI Chip chrome). With the FSD primitive
// at our disposal the two converge — render the same Badge at
// `size="sm"` so callers that imported `SimpleBadge` for the more
// compact look keep getting the more compact look.
export const SimpleBadge = ({ label, colorType, variant, ...rest }) => {
  // eslint-disable-next-line no-unused-vars
  const _variant = variant;
  const tone = resolveTone(colorType);
  return (
    <FSDBadge tone={tone} size='sm' {...rest}>
      {label}
    </FSDBadge>
  );
};

Badge.propTypes = {
  label: PropTypes.string,
  colorType: PropTypes.string,
  fixedWidth: PropTypes.bool,
  diagonalBorderRadius: PropTypes.bool,
  bottomLeftRadius: PropTypes.bool,
  customStyle: PropTypes.string,
};

